import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type SearchParams,
  type UseSearchHookResult,
} from '@saviynt/biz-entity-catalog';

import { listClients, normalizeClientListItem } from '../data-sources';
import { type Client } from '../types';

/**
 * Fetches ALL clients from the API (pagination only, no server-side
 * search/sort/filter) and applies search + enabled filter client-side.
 *
 * The list API does not support search, filter, or sort query params.
 * BEC still passes SearchParams with searchText and filters — we use
 * those for client-side filtering after fetching the full dataset.
 */
export function useClientSearch(
  params: SearchParams
): UseSearchHookResult<Client> {
  // Full dataset from the API (all pages accumulated)
  const [allData, setAllData] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [hasNextApiPage, setHasNextApiPage] = useState(false);

  const currentPageRef = useRef(0);
  const refetchCountRef = useRef(0);
  const [refetchTrigger, setRefetchTrigger] = useState(0);

  const paramsRef = useRef(params);

  paramsRef.current = params;

  const fetchPage = useCallback(
    async (pageNum: number, append: boolean) => {
      if (append) {
        setIsFetchingNextPage(true);
      } else {
        setIsLoading(true);
      }

      try {
        const result = await listClients({
          page: pageNum,
          size: paramsRef.current.size,
        });

        const items = result.data.map(normalizeClientListItem);

        setAllData((prev) => (append ? [...prev, ...items] : items));
        setHasNextApiPage(result.pagination.hasNext);
        currentPageRef.current = result.pagination.page;
      } finally {
        setIsLoading(false);
        setIsFetchingNextPage(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetchTrigger is an intentional cache-buster to force a re-fetch from external callers.
    [refetchTrigger]
  );

  // Fetch initial page on mount or when refetch is triggered
  useEffect(() => {
    currentPageRef.current = 0;
    fetchPage(0, false).catch(() => {});
  }, [fetchPage]);

  const fetchNextPage = useCallback(() => {
    if (hasNextApiPage && !isFetchingNextPage) {
      fetchPage(currentPageRef.current + 1, true).catch(() => {});
    }
  }, [hasNextApiPage, isFetchingNextPage, fetchPage]);

  const refetch = useCallback(() => {
    refetchCountRef.current += 1;
    setRefetchTrigger(refetchCountRef.current);
  }, []);

  // Client-side filtering: search text + enabled status
  const filteredData = useMemo(() => {
    let result = allData;

    // Apply enabled filter (client-side)
    const enabledFilterValues = params.filters?.enabled;

    if (enabledFilterValues && enabledFilterValues.length === 1) {
      const filterValue =
        enabledFilterValues[0] === 'true' || enabledFilterValues[0] === true;

      result = result.filter((c) => c.enabled === filterValue);
    }

    // Apply search text filter
    const search = (params.searchText ?? '').toLowerCase().trim();

    if (search) {
      result = result.filter(
        (c) =>
          c.clientName.toLowerCase().includes(search) ||
          c.clientId.toLowerCase().includes(search) ||
          (c.description ?? '').toLowerCase().includes(search)
      );
    }

    return result;
  }, [allData, params.searchText, params.filters?.enabled]);

  return {
    data: filteredData,
    totalItems: filteredData.length,
    totalPages: 1,
    page: 0,
    size: params.size,
    hasNext: hasNextApiPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  };
}
